import { Component, createRef } from 'preact';
import {
  ACCENT, ACCENT_TEXT, TEXT_PRIMARY, TEXT_MUTED,
  FourCorners, RADIUS,
} from '../lib/medievalTheme';
import { resolveFeaturedSlots, deckCardArt } from '../utils/sorcery/deckFeaturedCards';
import { playUI, UI } from '../utils/arena/uiSounds';
import ElementIcon from './deckStats/ElementIcon';
import { isPhoneLayout } from '../utils/platform';
import { t, k } from '../localization/t';

// The tile's whole visual system is authored in cqw against a ~22cqw
// grid cell. On phone the same proportions render at 0.72rem per design
// cqw, which lands three tiles per row on an iPhone-class landscape
// screen. Text takes a floor so labels stay readable at that scale.
const PHONE = isPhoneLayout();
export const T = (n) => (PHONE ? `${+(n * 0.72).toFixed(3)}rem` : `${n}cqw`);
export const TXT = (n, floor) => (PHONE ? `${Math.max(+(n * 0.72).toFixed(3), floor)}rem` : `${n}cqw`);

const ELEMENT_DISPLAY_ORDER = ['Water', 'Earth', 'Fire', 'Air'];

// A card's `elements` array is a flavor/affinity list. Multi-element
// "rainbow" sites (Avalon, Pristine Paradise) and legendaries (King Arthur,
// Azuridge Caravan) list every element while mechanically only demanding
// one or two. Aggregating that across the whole deck paints a 2-element
// deck with a single splash card as a 4-element deck. Identity is better
// derived from spellbook threshold demand. What the deck *requires* to
// cast. Which matches how the full deck-stats overlay computes it.
function computeDeckElements(deck, sorceryCards) {
  if (!Array.isArray(deck?.cards) || !Array.isArray(sorceryCards)) return [];
  const present = new Set();
  for (const entry of deck.cards) {
    const card = sorceryCards.find((c) => c.unique_id === entry.cardId);
    if (!card) continue;
    const zone = entry?.zone || (card._sorceryCategory === 'Spell' ? 'spellbook' : '');
    if (zone !== 'spellbook') continue;
    if (Number(card.waterThreshold) > 0) present.add('Water');
    if (Number(card.earthThreshold) > 0) present.add('Earth');
    if (Number(card.fireThreshold) > 0) present.add('Fire');
    if (Number(card.airThreshold) > 0) present.add('Air');
  }
  return ELEMENT_DISPLAY_ORDER.filter((name) => present.has(name));
}

function findAvatarUrl(deck, sorceryCards) {
  const cover = deckCardArt(deck, deck?.showcase?.cover, sorceryCards);
  if (cover) return cover.imageUrl;
  if (!deck?.cards || !Array.isArray(sorceryCards)) return null;
  // Imposter decks carry additional Avatar entries in the spellbook or
  // collection. Prefer the entry explicitly marked `zone: 'avatar'` so
  // the tile portrait reflects the equipped face, not the first Avatar
  // that happens to appear in the cards array.
  const primary = deck.cards.find((e) => e.zone === 'avatar');
  const candidates = primary ? [primary, ...deck.cards] : deck.cards;
  for (const entry of candidates) {
    const card = sorceryCards.find((c) => c.unique_id === entry.cardId);
    if (card?.type === 'Avatar') {
      const printing = card.printings?.find((p) => p.unique_id === entry.printingId) || card.printings?.[0];
      return printing?.image_url || null;
    }
  }
  return null;
}

/**
 * Deck-tile artwork with a loading skeleton. Both the cover portrait
 * and the fanned cards fetch full card art, so a gallery scroll used to
 * show empty frames that snapped to finished images. The skeleton sits
 * over the art (shared `.card-skeleton` shimmer) and is removed the
 * moment the image loads. No reveal animation, the art is just there.
 */
class TileImage extends Component {
  state = { loaded: false, failed: false };
  imgRef = createRef();

  componentDidMount() {
    // Already decoded at mount (cache hit, or a re-mount while scrolling):
    // the load event may have fired before this handler attached, which
    // would strand the skeleton.
    const img = this.imgRef.current;
    if (img?.complete && img.naturalWidth > 0) this.setState({ loaded: true });
  }

  componentDidUpdate(prevProps) {
    if (prevProps.src !== this.props.src && (this.state.loaded || this.state.failed)) {
      this.setState({ loaded: false, failed: false });
    }
  }

  render() {
    const { src, alt = '', className = '', style = {}, radius } = this.props;
    const { loaded, failed } = this.state;
    return (
      <>
        <img
          ref={this.imgRef}
          src={src}
          alt={alt}
          draggable={false}
          decoding="async"
          // Unlike the deck builder's card grid, the deck gallery isn't
          // virtualized. Every tile is in the DOM, so a 40-deck collection
          // would fire 160 art requests at mount and starve the ones on
          // screen. Lazy defers the off-screen rows; the skeleton covers
          // them until they arrive.
          loading="lazy"
          onLoad={() => this.setState({ loaded: true })}
          onError={() => this.setState({ failed: true })}
          className={className}
          style={style}
        />
        {!loaded ? (
          <div
            className="card-skeleton absolute inset-0"
            aria-hidden="true"
            style={{ borderRadius: radius, backgroundColor: '#14100b', zIndex: 2, opacity: failed ? 0.6 : 1 }}
          />
        ) : null}
      </>
    );
  }
}

function FannedCards({ cards, editing, activeSlot, onSlotClick }) {
  if (!cards || cards.length === 0) return null;
  return (
    <div className={`absolute ${editing ? '' : 'pointer-events-none'}`} style={{ top: T(0.55), left: T(1.8), width: T(12), height: T(9.5), zIndex: 10 }}>
      {cards.map((c, i) => (
        (!c && !editing) ? null : (
        <div
          key={`${c?.imageUrl || 'empty'}-${i}`}
          className={`absolute deck-fan-card deck-fan-card-${i}`}
          style={{
            left: 0,
            top: 0,
            width: T(4.7),
            aspectRatio: '63 / 88',
            zIndex: cards.length - i,
            filter: 'drop-shadow(0 3px 8px rgba(0,0,0,0.7))',
          }}
        >
          {c ? (
            <TileImage
              src={c.imageUrl}
              radius={RADIUS.card}
              // Fan order stagger. The cards resolve left to right rather
              // than all snapping at once.
              delay={i * 0.07}
              className="w-full h-full object-cover"
              // Inset hairline in pure white so the card edge reads as a
              // crisp printed border on any art, instead of the dark
              // outer border that bled into the drop-shadow.
              style={{
                borderRadius: RADIUS.card,
                outline: '1px solid rgba(255,255,255,0.1)',
                outlineOffset: '-1px',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08)',
              }}
            />
          ) : (
            <div className="w-full h-full" style={{ borderRadius: RADIUS.card, background: '#0c0a08' }} />
          )}
          {editing ? (
            <button
              type="button"
              className={`absolute inset-0 deck-slot-target${activeSlot === i ? ' is-active' : ''}`}
              title={t('Change this card')}
              onClick={(e) => { e.stopPropagation(); onSlotClick?.(i, e.currentTarget.getBoundingClientRect()); }}
              style={{ borderRadius: RADIUS.card }}
            />
          ) : null}
        </div>
        )
      ))}
    </div>
  );
}

export default class DeckTile extends Component {
  render() {
    const {
      deck,
      sorceryCards,
      onClick,
      topRightSlot,
      footerLeftSlot,
      footerRightSlot,
      bottomLeftSlot,
      overlay,
      // Picker surfaces mark the chosen tile; the gallery never sets this.
      selected,
      // Showcase editing. The gallery flips this on for one tile at a
      // time so its cover + fanned cards become pick targets.
      editingShowcase,
      activeSlot,
      onSlotClick,
      // Grid position, drives the staggered entrance. Undefined skips it.
      enterIndex,
    } = this.props;

    const avatarUrl = findAvatarUrl(deck, sorceryCards);
    const featured = resolveFeaturedSlots(deck, sorceryCards);
    const elements = computeDeckElements(deck, sorceryCards);

    // The tile is a plain div, so the delegated `data-sound` click handler
    // never sees it. The cues have to be explicit. Same pair the deck
    // builder uses on its card grid. Gated on the tile actually doing
    // something: showcase-editing mode and the read-only precon reveal
    // both render tiles that shouldn't answer to the pointer.
    const interactive = !editingShowcase && typeof onClick === 'function';

    return (
      <div
        className="group cursor-pointer"
        onClick={interactive ? (e) => { playUI(UI.EQUIP); onClick(e); } : undefined}
        onMouseEnter={interactive ? () => playUI(UI.HOVER, { volume: 0.4 }) : undefined}
      >
        <div
          className={`relative transition-transform duration-200 ease-out hover:scale-[1.015]${interactive ? ' active:scale-[0.98]' : ''}${typeof enterIndex === 'number' ? ' deck-tile-enter' : ''}`}
          style={{
            aspectRatio: '1.5 / 1',
            // Stagger caps at the first two rows; later tiles arrive
            // together so a long scroll-back doesn't wait on a cascade.
            animationDelay: typeof enterIndex === 'number' ? `${Math.min(enterIndex, 11) * 45}ms` : undefined,
          }}
        >
          {/* Clipped frame. Everything that needs to respect the
              rounded corners (art, fades, footer, slots) lives here so
              we can keep `overflow: hidden`. The fanned cards render
              outside this layer so the leftmost card can peek past the
              tile's left edge.

              A radial mask fades the frame's upper-left corner toward
              transparency so the fanned cards (which sit above this
              layer) look like they're peeking *over* the container
              rather than sitting flush inside it. The mask only
              affects this frame; the cards on top render at full
              opacity since they're siblings, not children. */}
          <div
            className="absolute inset-0 overflow-hidden"
            style={{
              background: '#0c0a08',
              borderRadius: T(0.6),
              border: `1px solid ${ACCENT} 0.55)`,
              // Layered medieval frame. Matches the dice modal / pre-con
              // picker treatment. Two-tier outer shadow for weight +
              // close separation, a soft gold halo, then the depth
              // stack: top edge highlight (light catches the upper
              // bevel), bottom edge recess (carved ridge underneath),
              // inner rim, and matching top/bottom inset vignettes.
              boxShadow: [
                '0 22px 52px rgba(0,0,0,0.85)',
                '0 8px 18px rgba(0,0,0,0.55)',
                '0 2px 4px rgba(0,0,0,0.6)',
                `0 0 32px ${ACCENT} 0.14)`,
                `inset 0 1px 0 rgba(255,232,170,0.22)`,
                `inset 0 -1px 0 rgba(0,0,0,0.75)`,
                `inset 0 0 0 1px ${ACCENT} 0.25)`,
                'inset 0 -22px 44px rgba(0,0,0,0.55)',
                'inset 0 18px 40px rgba(0,0,0,0.32)',
              ].join(', '),
              WebkitMaskImage: `radial-gradient(ellipse ${T(13)} ${T(7)} at 0% 0%, transparent 22%, rgba(0,0,0,0.55) 55%, black 92%)`,
              maskImage: `radial-gradient(ellipse ${T(13)} ${T(7)} at 0% 0%, transparent 22%, rgba(0,0,0,0.55) 55%, black 92%)`,
            }}
          >
            {/* Avatar background. Sorcery card images are roughly
                portrait, so `object-fit: cover` scales them by width
                and the image already fills the container horizontally.
                That makes `object-position-x` a no-op. Instead we
                physically translate the rendered image rightward so
                the avatar's focal character (typically centred in the
                source art) clears the fanned cards on the left. The
                left-side fade gradient covers the exposed dark band
                underneath. */}
            {avatarUrl ? (
              <TileImage
                src={avatarUrl}
                alt={deck?.name || ''}
                radius={T(0.6)}
                // `.deck-tile-art` owns the translate (plus a slow zoom
                // on hover), see index.css.
                className="absolute inset-0 w-full h-full deck-tile-art"
                style={{
                  objectFit: 'cover',
                  objectPosition: '50% 18%',
                  // Soft left-edge mask on the image itself so its
                  // border dissolves into the dark backdrop instead of
                  // forming a faint visible seam. Tight 4% fade. Wide
                  // enough to hide the edge, narrow enough not to eat
                  // visible art.
                  WebkitMaskImage: 'linear-gradient(90deg, transparent 0%, rgba(0,0,0,1) 4%)',
                  maskImage: 'linear-gradient(90deg, transparent 0%, rgba(0,0,0,1) 4%)',
                }}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center" style={{ background: `${ACCENT} 0.03)` }}>
                <span className="text-3xl" style={{ color: `${ACCENT} 0.12)` }}>&#9830;</span>
              </div>
            )}

            {/* Stone-noise patina laid over the avatar art, blended via
                `overlay` so it tints the image without obscuring the
                subject. Sells the "this is a forged plaque, not a
                printed sticker" feel and ties the tile visually to the
                rest of the medieval UI surfaces. */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: 'url("/tex-stone.webp"), url("/tex-noise.webp")',
                backgroundSize: 'cover, auto',
                backgroundBlendMode: 'overlay, overlay',
                mixBlendMode: 'overlay',
                opacity: 0.55,
              }}
            />

            {/* Inset depth ring. Same bevel + vignette treatment the
                outer frame has, re-applied as an overlay above the
                avatar so the depth survives behind the cover image. */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                borderRadius: T(0.6),
                boxShadow: [
                  'inset 0 1px 0 rgba(255,232,170,0.22)',
                  'inset 0 -1px 0 rgba(0,0,0,0.7)',
                  `inset 0 0 0 1px ${ACCENT} 0.22)`,
                  'inset 0 -22px 44px rgba(0,0,0,0.5)',
                  'inset 0 18px 40px rgba(0,0,0,0.32)',
                  'inset 9px 0 24px rgba(0,0,0,0.35)',
                  'inset -9px 0 24px rgba(0,0,0,0.28)',
                ].join(', '),
              }}
            />

            {/* Left-side fade. The avatar image starts at tile-x 20%
                (due to translateX) and has its own 4% mask fade, so it
                only becomes fully opaque around tile-x 24%. The overlay
                stays fully opaque through that whole zone so the
                image's edge is *never* directly visible. Only past 25%
                does the overlay begin its decay, revealing the
                already-fully-opaque art behind it. Eliminating the
                hairline seam that double-fade created. */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background: 'linear-gradient(90deg, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 25%, rgba(0,0,0,0.78) 32%, rgba(0,0,0,0.45) 42%, rgba(0,0,0,0.18) 53%, rgba(0,0,0,0.04) 62%, rgba(0,0,0,0) 70%)',
              }}
            />

            {/* Cover pick target. Covers the art but stops short of
                the footer band so the deck name stays readable. */}
            {/* Cover art is the whole tile, so its pick target is the
                whole frame. A small box floating over the art read as
                a stray card outline. The label is nudged right so it
                clears the fanned cards. */}
            {editingShowcase ? (
              <button
                type="button"
                className={`absolute inset-0 flex items-center justify-center arena-heading deck-slot-target is-frame${activeSlot === 'cover' ? ' is-active' : ''}`}
                title={t('Change the cover artwork')}
                onClick={(e) => { e.stopPropagation(); onSlotClick?.('cover', e.currentTarget.getBoundingClientRect()); }}
                style={{
                  zIndex: 15,
                  paddingLeft: T(11),
                  paddingBottom: T(3),
                  fontSize: T(0.7),
                  color: ACCENT_TEXT,
                  textShadow: '0 2px 4px rgba(0,0,0,0.95)',
                  borderRadius: T(0.6),
                }}
              >
                {t('Cover')}
              </button>
            ) : null}

            {/* Optional top-right slot */}
            {topRightSlot ? (
              <div className="absolute z-20 flex items-center gap-1.5" style={{ top: T(0.8), right: T(0.8) }}>{topRightSlot}</div>
            ) : null}

            {/* Element indicators. Anchored low in the footer band so
                they don't collide with the right-side author / views
                row when player names are long. Sits at the bottom edge
                of the footer rather than its vertical midline. */}
            {elements.length > 0 ? (
              <div
                className="absolute pointer-events-none flex items-center"
                style={{
                  gap: T(0.28),
                  left: '50%',
                  bottom: T(0.4),
                  transform: 'translateX(-50%)',
                  zIndex: 5,
                  // Smoked-glass pill so the tokens read as one badge
                  // instead of loose icons floating over the art.
                  // Uniform padding keeps the pill's end caps concentric
                  // with the round tokens (outer r = token r + padding).
                  padding: T(0.2),
                  borderRadius: 'var(--radius-full)',
                  background: 'rgba(6,4,2,0.55)',
                  backdropFilter: 'blur(6px)',
                  WebkitBackdropFilter: 'blur(6px)',
                  boxShadow: `inset 0 0 0 1px ${ACCENT} 0.22), inset 0 1px 0 rgba(255,232,170,0.12), 0 2px 6px rgba(0,0,0,0.5)`,
                }}
              >
                {elements.map((name) => (
                  <ElementIcon key={name} element={name} size={PHONE ? 24 : 17} />
                ))}
              </div>
            ) : null}

            {/* Bottom shadow fade */}
            <div
              className="absolute inset-x-0 bottom-0 pointer-events-none"
              style={{
                height: T(8.5),
                // Warm-black, eased stops. A neutral black fade went
                // grey against the sepia frame; this one matches the
                // tile's own backdrop and lands softly instead of
                // banding at the top edge.
                background: 'linear-gradient(to top, rgba(10,7,4,0.96) 0%, rgba(10,7,4,0.88) 22%, rgba(10,7,4,0.66) 42%, rgba(10,7,4,0.38) 60%, rgba(10,7,4,0.16) 78%, rgba(10,7,4,0.04) 92%, rgba(10,7,4,0) 100%)',
              }}
            />

            {/* Bottom info band. Above the cover pick target so the
                name field stays clickable while editing. */}
            <div
              className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-2"
              style={{ padding: `${T(0.7)} ${T(0.95)}`, minHeight: T(4.1), zIndex: 20 }}
            >
              <div className="min-w-0 flex-1">{footerLeftSlot}</div>
              <div className="shrink-0 text-right flex flex-col items-end gap-0.5">{footerRightSlot}</div>
            </div>

            {/* Bottom-left slot */}
            {bottomLeftSlot ? (
              <div className="absolute z-20" style={{ left: T(0.85), bottom: T(4.6) }}>{bottomLeftSlot}</div>
            ) : null}

            {/* Heraldic corner plating. Small gold L-brackets that
                bracket the rounded corners and read as a forged frame.
                Sits above all art/footer layers so the corners always
                stay legible. The upper-left ornament is naturally
                hidden by the radial mask where the fanned cards live,
                which is exactly the silhouette we want. */}
            <FourCorners color={`${ACCENT} 0.7)`} />

            {/* Hover glow */}
            <div
              className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-200"
              style={{
                borderRadius: T(0.6),
                boxShadow: `inset 0 0 0 1px ${ACCENT} 0.5), 0 0 28px ${ACCENT} 0.18)`,
              }}
            />

            {/* Selection ring. A brighter, always-on version of the
                hover glow so the picked tile stays obvious once the
                cursor moves away. */}
            {selected ? (
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  borderRadius: T(0.6),
                  boxShadow: `inset 0 0 0 2px ${ACCENT_TEXT}, inset 0 0 30px ${ACCENT} 0.28), 0 0 ${T(2.2)} ${ACCENT} 0.4)`,
                  zIndex: 25,
                }}
              />
            ) : null}
          </div>

          {/* Fanned cards. Rendered outside the clip so the leftmost
              card extends past the tile's left edge. Sits above every
              clipped layer including the bottom shadow, so it reads
              bright instead of being darkened by the footer gradient. */}
          <FannedCards
            cards={featured}
            editing={editingShowcase}
            activeSlot={activeSlot}
            onSlotClick={onSlotClick}
          />

          {/* Confirm / modal overlay. Clipped to the rounded corners
              so confirm dialogs don't leak outside the tile. */}
          {overlay ? (
            <div className="absolute inset-0 z-30 overflow-hidden" style={{ borderRadius: T(0.6) }}>{overlay}</div>
          ) : null}
        </div>
      </div>
    );
  }
}

export { findAvatarUrl };

const MODE_PILL = {
  sandbox: { label: k('Sandbox'), background: 'rgba(167,139,250,0.16)', color: '#c4b5fd', border: '1px solid rgba(167,139,250,0.35)', title: k('Sandbox deck. Not eligible for ranked play.') },
  peasant: { label: k('Peasant'), background: 'rgba(134,192,107,0.16)', color: '#a3d488', border: '1px solid rgba(134,192,107,0.35)', title: k('Peasant deck. Ordinary and Exceptional cards only. Ranked-eligible.') },
  constructed: { label: k('Constructed'), background: `${ACCENT} 0.1)`, color: ACCENT_TEXT, border: `1px solid ${ACCENT} 0.28)`, title: k('Constructed deck. Eligible for ranked play.') },
};

export function DeckModePill({ mode }) {
  const { label, title, ...style } = MODE_PILL[mode] || MODE_PILL.constructed;
  return (
    <span
      className="px-1.5 py-0.5 rounded-full font-semibold"
      style={{ fontSize: TXT(0.5, 0.625), ...style }}
      title={t(title)}
    >
      {t(label)}
    </span>
  );
}

export const DeckTileText = {
  title: (text) => (
    <div className="arena-heading font-bold truncate" style={{ color: TEXT_PRIMARY, fontSize: TXT(0.82, 0.875), lineHeight: 1.15, textShadow: '0 2px 6px rgba(0,0,0,0.9)' }}>{text}</div>
  ),
  sub: (text) => (
    <div className="truncate tabular-nums" style={{ color: TEXT_MUTED, fontSize: TXT(0.56, 0.75), marginTop: T(0.18) }}>{text}</div>
  ),
  accent: (text) => (
    <div className="truncate" style={{ color: ACCENT_TEXT, fontSize: TXT(0.56, 0.75) }}>{text}</div>
  ),
};
